<template>
    <div>
        <slot name="trigger">
            <button class="button button--blank" type="button" @click="toggleModal">
                {{ $t('notes.item.title') }}
            </button>
        </slot>
        <Modal
            v-if="isOpen"
            name="notes-edit"
            :title="$t('notes.item.title')"
            :direction="textDirection"
            @closed="isOpen = false"
        >
            <template #content>
                <Spinner v-if="loading" />
                <div class="notes-edit">
                    <div class="notes-edit__text">
                        {{ note.book_part_v2.raw_text }}
                    </div>
                    <form id="notes-edit" class="notes-edit__form" @submit.prevent="save">
                        <div class="form-group">
                            <textarea
                                v-model="comment"
                                class="notes-edit__comment form-group__textarea"
                                :placeholder="$t('notes.add.commentPlaceholder')"
                                required="true"
                            />
                        </div>
                        <TransitionExpand v-if="error">
                            <ErrorList :error="error" />
                        </TransitionExpand>
                    </form>
                </div>
            </template>
            <template #bottom>
                <div class="modal__confirm">
                    <button
                        type="button"
                        class="button button--gray modal__confirm-button"
                        @click.prevent="isOpen = false"
                    >
                        {{ $t('notes.add.cancel') }}
                    </button>
                    <button class="button modal__confirm-button" type="submit" form="notes-edit">
                        {{ $t('notes.add.save') }}
                    </button>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { ErrorList } from '@speechki-frontend/shared/ui/forms';
import TransitionExpand from '@speechki-frontend/shared/ui/TransitionExpand/TransitionExpand.vue';
import { noteUpdate } from '../../api/notes';
import { mapState } from 'vuex';
import { isRtlLanguage } from '@speechki-frontend/core/editor/utils';

export default {
    name: 'NotesEdit',
    components: {
        Modal,
        TransitionExpand,
        ErrorList,
    },
    props: {
        note: {
            required: true,
            type: Object,
        },
    },
    data() {
        return {
            isOpen: false,
            loading: false,
            comment: '',
            error: null,
        };
    },
    computed: {
        audio() {
            const {
                audio_duration: end,
                audio: url = null,
                audio_generation_extra,
            } = this.note.book_part_v2;

            const chunks = (audio_generation_extra && audio_generation_extra.trim_chunks) || [];

            if (chunks.length) {
                const { start, end } = chunks[0];
                return { start, end, url };
            }

            return { start: 0, end, url };
        },
        ...mapState({
            book: (state) => state.book.book,
        }),
        textDirection() {
            return isRtlLanguage(this.book.book_language) ? 'rtl' : 'ltr';
        },
    },
    watch: {
        isOpen() {
            if (this.isOpen) {
                this.comment = this.note.comment;
                this.error = null;
            }
        },
    },
    methods: {
        toggleModal() {
            this.isOpen = !this.isOpen;
        },
        async save() {
            try {
                this.loading = true;
                this.error = null;

                const { id } = this.note;
                const data = {
                    book: this.note.bookId,
                    local_id: this.note.local_id,
                    comment: this.comment,
                    book_part_v2: this.note.book_part_v2.id,
                    start: this.note.start,
                    end: this.note.end,
                };

                const note = await noteUpdate(id, data);

                this.$store.dispatch('pushToast', {
                    id: Date.now(),
                    type: 'success',
                    title: this.$t('notes.item.success'),
                });

                this.$emit('saved', note);

                this.isOpen = false;
            } catch (e) {
                console.log(e);
                this.error = e;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style src="./NotesEdit.scss" lang="scss"></style>
